import { forwardRef } from 'react'
import { useCommitmentMatrixDashboard } from '@/api/queries/commitment-matrix-queries'
import kripLogo from '@/assets/KAEFER_Logo_www_pos.svg'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import type {
	CommitmentMatrixDashboardCollaboratorStatus,
	CommitmentMatrixDashboardParams,
} from '@/types'
import { formatPercentage } from '@/utils/format-percentage'

const STATUS_PILL_CLASS: Record<
	CommitmentMatrixDashboardCollaboratorStatus,
	string
> = {
	on_track: 'bg-kaefer-grass-green/15 text-kaefer-forest-green',
	in_progress: 'bg-kaefer-honey-yellow/20 text-kaefer-dark-grey',
	not_started: 'bg-kaefer-red/15 text-kaefer-red',
	aboned: 'bg-kaefer-sapphire-blue/15 text-kaefer-sapphire-blue',
}

function pillClassForPct(pct: number): string {
	if (pct >= 100) return 'bg-kaefer-grass-green/15 text-kaefer-forest-green'
	if (pct >= 50) return 'bg-kaefer-honey-yellow/20 text-kaefer-dark-grey'
	return 'bg-kaefer-red/15 text-kaefer-red'
}

interface CollaboratorsPrintSheetProps {
	params: CommitmentMatrixDashboardParams
}

function PrintHeader({
	now,
	rangeLabel,
}: {
	now: Date
	rangeLabel: string | null
}) {
	return (
		<div className="space-y-1 pb-4">
			<div className="flex items-center border-b border-kaefer-light-silver pb-2">
				<img alt="Logo KAEFER" className="h-10 w-auto" src={kripLogo} />
				<h1 className="flex-1 text-center text-base font-semibold text-kaefer-dark-grey">
					Matriz de Comprometimento — Colaboradores
				</h1>
				<div className="h-10 w-auto opacity-0" aria-hidden="true">
					<img alt="" className="h-10 w-auto" src={kripLogo} />
				</div>
			</div>
			<p className="text-center text-xs text-muted-foreground">
				Documento gerado em {now.toLocaleDateString('pt-BR')}
				{rangeLabel ? ` — período: ${rangeLabel}` : ''}
			</p>
		</div>
	)
}

/**
 * Papel timbrado com a tabela de colaboradores do dashboard da Matriz de
 * Comprometimento — renderizado no preview e formatado para impressão/PDF
 * em paisagem via `react-to-print` (ver CollaboratorsPrintModal).
 */
export const CollaboratorsPrintSheet = forwardRef<
	HTMLDivElement,
	CollaboratorsPrintSheetProps
>(({ params }, ref) => {
	const now = new Date()
	const { data, isLoading } = useCommitmentMatrixDashboard({
		...params,
		page: 1,
		pageSize: 1000,
	})

	const rangeLabel =
		params.rangeStart && params.rangeEnd
			? `${formatDate(params.rangeStart)} — ${formatDate(params.rangeEnd)}`
			: null

	const items = data?.collaborators?.items ?? []

	return (
		<div ref={ref} className="bg-white p-2 print:p-7">
			{isLoading || !data ? (
				<div className="space-y-4">
					<PrintHeader now={now} rangeLabel={rangeLabel} />
					<div className="space-y-2">
						<Skeleton className="h-8 w-full" />
						<Skeleton className="h-8 w-full" />
						<Skeleton className="h-8 w-full" />
					</div>
				</div>
			) : (
				<table className="w-full border-collapse text-left text-xs">
					<thead>
						<tr>
							<th colSpan={10} className="p-0 text-left font-normal">
								<PrintHeader now={now} rangeLabel={rangeLabel} />
							</th>
						</tr>
						<tr className="border-b border-kaefer-light-silver text-[10px] uppercase text-muted-foreground">
							<th className="py-1.5 pr-2 font-medium">Matrícula</th>
							<th className="py-1.5 pr-2 font-medium">Nome</th>
							<th className="py-1.5 pr-2 font-medium">Cargo</th>
							<th className="py-1.5 pr-2 font-medium">CR</th>
							<th className="py-1.5 pr-2 text-right font-medium">Meta</th>
							<th className="py-1.5 pr-2 text-right font-medium">VEA</th>
							<th className="py-1.5 pr-2 text-right font-medium">DIR</th>
							<th className="py-1.5 pr-2 text-right font-medium">INT</th>
							<th className="py-1.5 pr-2 text-right font-medium">CA</th>
							<th className="py-1.5 text-right font-medium">%</th>
						</tr>
					</thead>
					<tbody>
						{items.length === 0 ? (
							<tr>
								<td
									colSpan={10}
									className="py-6 text-center text-muted-foreground"
								>
									Nenhum colaborador encontrado para os filtros selecionados.
								</td>
							</tr>
						) : (
							items.map((c) => (
								<tr
									key={c.personId}
									className="break-inside-avoid border-b border-kaefer-light-silver/50 last:border-0"
								>
									<td className="py-1.5 pr-2 text-muted-foreground">
										{c.registration}
									</td>
									<td className="py-1.5 pr-2 font-medium text-kaefer-dark-grey">
										{c.name}
									</td>
									<td className="py-1.5 pr-2 text-muted-foreground">
										{c.jobRole ?? '—'}
									</td>
									<td className="py-1.5 pr-2 text-muted-foreground">
										{c.crName}
									</td>
									<td className="py-1.5 pr-2 text-right">
										{c.target}
										{c.hasException && (
											<span className="ml-1 text-muted-foreground line-through">
												{c.originalTarget}
											</span>
										)}
									</td>
									<td className="py-1.5 pr-2 text-right">{c.vereAgirCount}</td>
									<td className="py-1.5 pr-2 text-right">
										{c.direcionadaCount}
									</td>
									<td className="py-1.5 pr-2 text-right">{c.interacaoCount}</td>
									<td className="py-1.5 pr-2 text-right">
										{c.cartaoAmareloCount}
									</td>
									<td className="py-1.5 text-right">
										<span
											className={cn(
												'inline-flex rounded-full px-2 py-0.5 font-semibold',
												STATUS_PILL_CLASS[c.status] ??
													pillClassForPct(c.adherencePct),
											)}
										>
											{formatPercentage(c.adherencePct)}
										</span>
									</td>
								</tr>
							))
						)}
					</tbody>
					<tfoot>
						<tr>
							<td colSpan={10} className="p-0">
								<p className="pt-2 text-center text-[10px] text-muted-foreground">
									Documento gerado automaticamente pelo sistema SSMA.
								</p>
							</td>
						</tr>
					</tfoot>
				</table>
			)}
		</div>
	)
})

CollaboratorsPrintSheet.displayName = 'CollaboratorsPrintSheet'

function formatDate(iso: string): string {
	const [year, month, day] = iso.split('-')
	return `${day}/${month}/${year}`
}
